import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as tienda from '@/api/tienda.api';
import * as facturacion from '@/api/facturacion.api';
import type { Factura, TipoDeNcf } from '@/api/facturacion.api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { useToast } from '@/hooks/useToast';
import { dinero, dineroCent } from '@/lib/formato-tienda';

/**
 * FACTURAR UNA VENTA.
 *
 * Sale siempre de una venta: de ahí vienen las piezas, las tallas y los
 * precios, y no se vuelven a escribir. Aquí se completa a nombre de quién va
 * el papel y, si el negocio factura con ITBIS, lo fiscal: el comprobante, hasta
 * cuándo se paga y si el cliente retiene.
 *
 * Las cuentas (base, ITBIS, neto) las hace el servidor y se enseñan antes de
 * emitir: aquí no se calcula un centavo.
 *
 * Si la venta era un borrador, facturarla la confirma: descuenta la
 * existencia, y si una talla no alcanza no se emite nada.
 */

export function EmitirFactura({
  ventaId,
  onCerrar,
  onEmitida,
}: {
  /** Sin venta, se elige una de las que todavía no tienen factura. */
  ventaId?: string;
  onCerrar: () => void;
  onEmitida: (f: Factura) => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [elegida, setElegida] = useState(ventaId ?? '');
  const [nombre, setNombre] = useState('');
  const [documento, setDocumento] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [ncfTipo, setNcfTipo] = useState<TipoDeNcf | ''>('');
  const [notas, setNotas] = useState('');
  const [vence, setVence] = useState('');
  const [retItbis, setRetItbis] = useState(false);
  const [retIsr, setRetIsr] = useState(false);
  const [isrTasa, setIsrTasa] = useState(10);

  const { data: ventas } = useQuery({
    queryKey: ['ventas', 'todas'],
    queryFn: () => tienda.listarVentas({ limit: 200 }),
    enabled: !ventaId,
  });
  const porFacturar = (ventas ?? []).filter((v) => v.estado !== 'ANULADA' && !v.factura);

  const { data: venta } = useQuery({
    queryKey: ['venta', elegida],
    queryFn: () => tienda.leerVenta(elegida),
    enabled: Boolean(elegida),
  });
  const { data: estado } = useQuery({
    queryKey: ['facturacion', 'estado'],
    queryFn: facturacion.estadoDeFacturacion,
  });
  const conItbis = (estado?.itbis ?? 0) > 0;
  const ret = { retItbis: conItbis && retItbis, retIsr: conItbis && retIsr, isrTasa };
  const { data: cuentas } = useQuery({
    queryKey: ['facturacion', 'previa', elegida, ret.retItbis, ret.retIsr, isrTasa],
    queryFn: () => facturacion.previaDeFactura(elegida, ret),
    enabled: Boolean(elegida) && conItbis,
  });

  // Al cambiar de venta, el papel arranca con los datos de SU clienta.
  useEffect(() => {
    if (!venta) return;
    setNombre(venta.cliente?.nombre ?? '');
    setTelefono(venta.cliente?.telefono ?? '');
    setDocumento(venta.cliente?.documento ?? '');
    setDireccion(venta.direccion ?? '');
  }, [venta]);

  const emitir = useMutation({
    mutationFn: () =>
      facturacion.emitirFactura({
        saleId: elegida,
        clienteNombre: nombre,
        clienteDocumento: documento || null,
        clienteTelefono: telefono || null,
        clienteDireccion: direccion || null,
        ncfTipo: ncfTipo || null,
        notas: notas || null,
        vence: vence || null,
        ...(ret.retItbis || ret.retIsr ? ret : {}),
      }),
    onSuccess: (f) => {
      toast.success(`Factura ${f.numero} emitida.`);
      for (const k of ['facturas', 'facturacion', 'ventas', 'venta', 'inventario', 'productos', 'tienda']) {
        void qc.invalidateQueries({ queryKey: [k] });
      }
      onEmitida(f);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo emitir la factura'),
  });

  const borrador = venta?.estado === 'BORRADOR';
  const piezas = venta?.lineas.reduce((a, l) => a + l.cantidad, 0) ?? 0;
  const bruto = venta?.lineas.reduce((a, l) => a + l.unitPrice * l.cantidad - l.descuento, 0) ?? 0;
  const aPagar = venta
    ? borrador
      ? Math.max(0, bruto - venta.descuento) + venta.envio
      : venta.total
    : 0;
  const tiposDeNcf = (estado?.ncf ?? []).filter((n) => n.quedan > 0);
  const pideRnc = tiposDeNcf.find((n) => n.tipo === ncfTipo)?.requiereRnc ?? false;
  const faltaRnc = pideRnc && !documento.trim();
  const retiene = cuentas && cuentas.retItbis + cuentas.retIsr > 0;

  return (
    <Modal open onClose={onCerrar} title="Nueva factura" size="lg">
      <div className="space-y-4">
        {!ventaId && (
          <Select
            label="Venta a facturar"
            value={elegida}
            onChange={(e) => setElegida(e.target.value)}
          >
            <option value="">
              {porFacturar.length === 0 ? 'No hay ventas sin facturar' : 'Elige una venta…'}
            </option>
            {porFacturar.map((v) => (
              <option key={v.id} value={v.id}>
                {v.numero} · {v.cliente?.nombre ?? 'sin clienta'} ·{' '}
                {v.estado === 'BORRADOR' ? 'borrador' : dinero(v.total)}
              </option>
            ))}
          </Select>
        )}

        {!ventaId && porFacturar.length === 0 && ventas && (
          <p className="text-sm text-slate-500">
            La factura sale de una venta. Registra primero la venta en «Ventas» y vuelve aquí.
          </p>
        )}

        {venta && (
          <>
            <div className="rounded-lg border border-surface-border p-3 text-sm">
              {venta.lineas.map((l) => (
                <div key={l.id} className="flex justify-between gap-3 py-0.5">
                  <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">
                    <span className="tabular-nums text-slate-500">{l.cantidad}×</span>{' '}
                    {l.productVariant.product.name}
                    <span className="text-slate-500"> · {l.productVariant.label}</span>
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {dinero(l.unitPrice * l.cantidad - l.descuento)}
                  </span>
                </div>
              ))}
              {cuentas && (
                <div className="mt-2 space-y-0.5 border-t border-surface-border pt-2 text-slate-600 dark:text-slate-300">
                  <Cuenta etiqueta="Base" valor={dineroCent(cuentas.sub)} />
                  <Cuenta etiqueta={`ITBIS ${cuentas.itbisTasa}% (incluido)`} valor={dineroCent(cuentas.itbis)} />
                </div>
              )}
              <div className="mt-2 flex justify-between border-t border-surface-border pt-2 font-semibold text-slate-900 dark:text-slate-100">
                <span>
                  Total · {piezas} pieza{piezas === 1 ? '' : 's'}
                </span>
                <span className="tabular-nums">{dinero(aPagar)}</span>
              </div>
              {retiene && (
                <div className="mt-2 space-y-0.5 border-t border-surface-border pt-2 text-slate-600 dark:text-slate-300">
                  {cuentas.retItbis > 0 && (
                    <Cuenta etiqueta="Retiene ITBIS" valor={`−${dineroCent(cuentas.retItbis)}`} />
                  )}
                  {cuentas.retIsr > 0 && (
                    <Cuenta etiqueta={`Retiene ISR ${isrTasa}%`} valor={`−${dineroCent(cuentas.retIsr)}`} />
                  )}
                  <div className="flex justify-between font-semibold text-slate-900 dark:text-slate-100">
                    <span>Neto a recibir</span>
                    <span className="tabular-nums">{dineroCent(cuentas.total)}</span>
                  </div>
                </div>
              )}
            </div>

            {borrador && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                Esta venta está en borrador. Al facturarla se confirma y{' '}
                {piezas === 1 ? 'la pieza sale' : `las ${piezas} piezas salen`} del inventario.
              </p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="A nombre de"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre del cliente"
              />
              <Input
                label={pideRnc ? 'RNC del cliente' : 'RNC o cédula (opcional)'}
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
                inputMode="numeric"
                placeholder="9 u 11 dígitos"
                error={faltaRnc ? 'El crédito fiscal lleva el RNC de quien compra.' : undefined}
              />
              <Input
                label="Teléfono"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                inputMode="tel"
              />
              <Input
                label="Dirección"
                value={direccion}
                onChange={(e) => setDireccion(e.target.value)}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {tiposDeNcf.length > 0 && (
                <Select
                  label="Comprobante fiscal"
                  value={ncfTipo}
                  onChange={(e) => setNcfTipo(e.target.value as TipoDeNcf | '')}
                >
                  <option value="">Sin comprobante fiscal</option>
                  {tiposDeNcf.map((n) => (
                    <option key={n.tipo} value={n.tipo}>
                      {n.tipo} · {n.nombre} · quedan {n.quedan}
                    </option>
                  ))}
                </Select>
              )}
              <Input
                label="Se paga hasta"
                type="date"
                value={vence}
                onChange={(e) => setVence(e.target.value)}
                hint={
                  vence ? undefined : `Sin fecha: ${estado?.diasDeVencimiento ?? 30} días desde hoy.`
                }
              />
            </div>

            {conItbis && (
              <div className="space-y-2 rounded-lg border border-surface-border p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Retenciones (si el cliente retiene)
                </p>
                <Interruptor
                  titulo="Retiene el ITBIS"
                  ayuda="El cliente se queda con el ITBIS entero y lo deposita él a la DGII."
                  checked={retItbis}
                  onChange={setRetItbis}
                />
                <Interruptor
                  titulo="Retiene ISR"
                  ayuda="Un porcentaje de la base, sin el ITBIS."
                  checked={retIsr}
                  onChange={setRetIsr}
                />
                {retIsr && (
                  <Select
                    label="Tasa de ISR"
                    className="max-w-[8rem]"
                    value={String(isrTasa)}
                    onChange={(e) => setIsrTasa(Number(e.target.value))}
                  >
                    {(estado?.tasasDeIsr ?? [10]).map((t) => (
                      <option key={t} value={t}>
                        {t}%
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            )}

            <Input
              label="Nota en la factura (opcional)"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            onClick={() => emitir.mutate()}
            disabled={!venta || !nombre.trim() || faltaRnc || emitir.isPending}
          >
            {emitir.isPending ? 'Emitiendo…' : 'Emitir factura'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Cuenta({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span>{etiqueta}</span>
      <span className="tabular-nums">{valor}</span>
    </div>
  );
}

function Interruptor({
  titulo,
  ayuda,
  checked,
  onChange,
}: {
  titulo: string;
  ayuda: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{titulo}</p>
        <p className="text-xs text-slate-500">{ayuda}</p>
      </div>
      <Toggle label={titulo} checked={checked} onChange={onChange} />
    </div>
  );
}
